import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AuthSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [componentId] = useState(Math.random().toString(36).substring(2, 9));

  useEffect(() => {
    const handleAuthSuccess = async () => {
      try {
        console.log(`[AuthSuccess:${componentId}] Starting authentication process`);
        const params = new URLSearchParams(location.search);
        const token = params.get('token');

        if (!token) {
          console.error(`[AuthSuccess:${componentId}] No token found in URL`);
          setError('Authentication failed: No token received');
          return;
        }

        console.log(`[AuthSuccess:${componentId}] Token received:`, token);
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.error(`[AuthSuccess:${componentId}] Invalid token format`);
          setError('Authentication failed: Invalid token format');
          return;
        }

        try {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log(`[AuthSuccess:${componentId}] Token decoded successfully:`, payload);

          // Store token and user data
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(payload));

          // Update context
          console.log(`[AuthSuccess:${componentId}] Updating auth context with user:`, payload);
          setUser(payload);

          // Set redirecting state and navigate after a short delay
          console.log(`[AuthSuccess:${componentId}] Setting redirect state`);
          setRedirecting(true);
          setTimeout(() => {
            console.log(`[AuthSuccess:${componentId}] Navigating to home`);
            navigate('/', { replace: true });
          }, 1000);
        } catch (decodeError) {
          console.error(`[AuthSuccess:${componentId}] Error decoding token:`, decodeError);
          setError('Authentication failed: Invalid token data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } catch (error) {
        console.error(`[AuthSuccess:${componentId}] Error during authentication:`, error);
        setError('Authentication failed: ' + (error.message || 'Unknown error'));
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    };

    handleAuthSuccess();
  }, [navigate, setUser, location.search, componentId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Authentication Error</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Return to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Redirecting...</h2>
            <p className="mt-2 text-gray-600">Please wait while we complete your authentication.</p>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthSuccess; 